/* eslint-disable camelcase */
import HideCrisp from '@components/atoms/HideCrisp';
import Section from '@components/atoms/Section';
import { Heading2 } from '@components/atoms/Typography';
import GridLinkList from '@components/molecules/list/GridLinkList';
import PageContainer from '@components/molecules/PageContainer';
import PostItem from '@components/molecules/PostItem';
import RichTextSection from '@components/molecules/RichTextSection';
import SliceZone from '@components/molecules/SliceZone';
import FixedGetQuoteButton from '@components/organisms/buttons/FixedGetQuoteButton';
import CarriersLogoList from '@components/organisms/CarriersLogoList';
import DefaultHeader from '@components/organisms/headers/DefaultHeader';
import JoinSection from '@components/organisms/sections/JoinSection';
import COLORS from '@lib/constants/colors';
import env from '@lib/constants/env';
import LINKS from '@lib/constants/links';
import ROUTES from '@lib/constants/routes';
import { aggregateRating } from '@lib/constants/seo';
import { PrismicDocument } from '@lib/data/prismic';
import { getInsurancePageInfo, InsurancePageId } from '@lib/helpers/texts';
import prisma from '@lib/services/prisma';
import { PrismicClient } from '@lib/services/prismic';
import { GetStaticPaths, GetStaticProps } from 'next';
import { ArticleJsonLd, BreadcrumbJsonLd, ProductJsonLd } from 'next-seo';
import React from 'react';
import styled from 'styled-components';
import { Post, SliceBody } from 'types/frontEndTypes';

const PostsContainer = styled.div`
  @media screen and (max-width: 768px) {
    padding: 32px 8px;
  }

  position: relative;
  margin: 32px 0 0;
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
`;

type PageDoc = PrismicDocument<{
  headline: string;
  subtitle: string;
  seo_title: string;
  seo_description: string;
  cta: string;
  image: {
    alt: string;
    url: string;
  };

  posts: {
    post: {
      id: string;
    };
  }[];

  body: SliceBody[];
}>;

interface Props {
  doc: PageDoc;
  brands: {
    brand: string;
    brandSlug: string;
  }[];
  id: InsurancePageId;
  posts: Post[];
  preview: boolean;
}

const Page = ({ doc, brands, id, posts, preview }: Props) => {
  const info = getInsurancePageInfo(id);

  if (!info) return null;

  return (
    <PageContainer
      title={doc.data.seo_title}
      metaDescription={doc.data.seo_description}
      customAmpPage
      transparentNavbar
      transparentNavbarFontColor={COLORS.black}
      preview={preview}
    >
      <HideCrisp />
      <ProductJsonLd
        productName={info?.productName}
        description={doc.data.seo_description}
        aggregateRating={aggregateRating}
      />
      <ArticleJsonLd
        url={`${env.baseUrl}/${doc.uid}`}
        title={info.productName}
        description={doc.data.seo_description}
        datePublished={doc.first_publication_date || ''}
        dateModified={doc.last_publication_date || ''}
        authorName={['Luna Seguros']}
        publisherName="Luna Seguros"
        publisherLogo={LINKS.logo}
        images={[doc.data.image.url || '']}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Luna',
            item: env.baseUrl,
          },
          {
            position: 2,
            name: `Seguro para ${info?.name}`,
            item: `${env.baseUrl}/${doc.uid}`,
          },
        ]}
      />
      <DefaultHeader
        title={doc.data.headline}
        subtitle={doc.data.subtitle}
        buttonText={doc.data.cta}
        image={doc.data.image}
        pastel
      />
      <CarriersLogoList />
      <RichTextSection>
        <SliceZone sliceZone={doc.data.body} />
      </RichTextSection>

      <Section pastel>
        <Heading2>Compare o seguro auto para marcas de {info.name}</Heading2>
        <GridLinkList
          deskTemplate="1fr 1fr 1fr 1fr"
          links={brands.map((brand) => ({
            path: ROUTES.insurancePage.brand(id, brand.brandSlug),
            label: `Seguro ${info.name} ${brand.brand}`,
          }))}
        />
      </Section>

      <Section>
        <Heading2 center>Tire suas dúvidas sobre {info.productName}</Heading2>

        <PostsContainer>
          {posts.map((post) => (
            <PostItem post={post} key={post.id} />
          ))}
        </PostsContainer>
      </Section>

      <JoinSection
        pastel
        buttonText="Quero Economizar"
        title="O jeito fácil de cotar o seu seguro auto"
      />
      <FixedGetQuoteButton />
    </PageContainer>
  );
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: ['/seguro-carro', '/seguro-moto', '/seguro-caminhao'],
  fallback: false,
});

export const getStaticProps: GetStaticProps<Props> = async ({
  preview = false,
  previewData,
  params,
}) => {
  const { insurancePage } = params as {
    insurancePage: InsurancePageId;
  };

  const dic = getInsurancePageInfo(insurancePage);

  if (!dic) return { notFound: true };

  const doc = (await PrismicClient.getByUID(
    'insurance_page',
    insurancePage,
    previewData as any,
  )) as PageDoc;

  if (!doc) return { notFound: true };

  const [posts, brands] = await Promise.all([
    PrismicClient.getByIDs(
      doc.data.posts.map(({ post }) => post.id),
      previewData as any,
    ),
    prisma.fipeModel.findMany({
      where: {
        vehicleType: dic.type,
      },
      distinct: ['brand'],
      orderBy: { brand: 'asc' },
      select: {
        brand: true,
        brandSlug: true,
      },
    }),
  ]);

  return {
    props: {
      preview,
      doc: doc || null,
      brands,
      id: insurancePage,
      posts: posts.results as Post[],
    },
    revalidate: 3600,
  };
};

export default Page;
